import { faMedal, faEnvelope, faTimesCircle, faCheckCircle } from '@fortawesome/free-solid-svg-icons'
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import classNames from 'classnames'
import i18next from 'i18next'
import PropTypes from 'prop-types'
import Skeleton from 'react-loading-skeleton'
import useSession from '../../hooks/useSession'
import useModal from '../../hooks/useModal'
import useToast from '../../hooks/useToast'
import recruiterFilteredCategories from '../../services/recruiterFilteredCategories'
import Container from '../atoms/Container'
import Title from '../atoms/Title'
import CardToggle from '../molecules/CardToggle'
import Pager from '../molecules/Pager'
import Repeat from '../atoms/Repeat'
import Checkbox from '../atoms/Checkbox'
import CompetitionInvitationModal from '../modals/CompetitionInvitationModal'
import Button from '../atoms/Button'

function UserTopScore({ categoriesList, onLoadingChange }) {
  const { session } = useSession()
  const { createModal, removeModal } = useModal()
  const { createToast, toastElement } = useToast()

  const [page, setPage] = useState(0)
  const ranksPerPage = parseInt(process.env.REACT_APP_RANKS_PER_PAGE || '10', 10)

  const [selectedTalents, setSelectedTalents] = useState([])

  const {
    isLoading,
    isError,
    error,
    data
  } = useQuery({
    queryKey: ['categoriesRanking', session, categoriesList, page],
    queryFn: () => recruiterFilteredCategories(page + 1, categoriesList, session, i18next.language),
    enabled: !!session && categoriesList.length > 0
  })

  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(isLoading)
    }
  }, [isLoading, onLoadingChange])

  const handleRowCheckbox = useCallback((checked, GUID) => {
    setSelectedTalents((prev) => {
      if (checked) {
        return [...prev, GUID]
      }
      return prev.filter((id) => id !== GUID)
    })
  }, [])

  const handleSelectAll = () => {
    if (!data?.rankingByCat) return

    const allGUIDs = data.rankingByCat.map((item) => item.GUID)
    if (selectedTalents.length === allGUIDs.length) {
      setSelectedTalents([])
    } else {
      setSelectedTalents(allGUIDs)
    }
  }

  const handleInvite = (type) => {
    if (selectedTalents.length === 0) {
      createToast('error', 'Debes seleccionar por lo menos un talento para enviarle una invitación')
      return
    }

    const onAccept = () => {
      createToast('success', 'Invitación enviada correctamente')
      removeModal()
    }

    const onCancel = () => {
      removeModal()
    }

    createModal(
      <CompetitionInvitationModal
        numberOfTalentsSelected={selectedTalents.length}
        invitationType={type}
        onAccept={onAccept}
        onCancel={onCancel}
      />
    )
  }

  if (isError) {
    return (
      <div className="w-full flex flex-col gap-5">
        <Title size="xl">{i18next.t('scoreboard')}</Title>
        <p>{error?.message}</p>
      </div>
    )
  }

  // Check if all talents are selected
  const allGUIDs = data?.rankingByCat?.map((item) => item.GUID) || []
  const allSelected = selectedTalents.length > 0 && selectedTalents.length === allGUIDs.length

  return (
    <div className="w-full flex flex-col gap-5">

      {toastElement}

      <Title size="xl">{i18next.t('scoreboard')}</Title>

      <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
        <Button
          onClick={() => handleInvite('email')}
          color="blue"
          icon={faEnvelope}
          className="w-full"
        >
          Enviar invitación por Email
        </Button>
        <Button
          onClick={() => handleInvite('whatsapp')}
          color="green"
          icon={faWhatsapp}
          className="w-full"
        >
          Enviar invitación por WhatsApp
        </Button>
      </div>

      <Container>
        <div className="w-full divide-y">
          <div className="w-full grid grid-cols-12 gap-4 p-2 items-center">
            <div className="col-span-1 flex justify-center mr-2">
              <Button
                color={allSelected ? 'red' : 'green'}
                onClick={handleSelectAll}
                title={allSelected ? "Deseleccionar todos" : "Seleccionar todos"}
                icon={allSelected ? faTimesCircle : faCheckCircle}
                circle
              />
            </div>

            <div className="col-span-1 text-center font-bold text-sm md:text-base">
              {i18next.t('top')}
            </div>
            <div className="col-span-3 text-center font-bold text-sm md:text-base">
              {i18next.t('user')}
            </div>
            <div className="col-span-1 text-center font-bold text-sm md:text-base">
              {i18next.t('score')}
            </div>
            <div className="col-span-6 text-center font-bold text-sm md:text-base">
              {i18next.t('category')}
            </div>
          </div>

          {isLoading && (
            <Repeat count={ranksPerPage}>
              <div className="w-full grid grid-cols-12 gap-4 p-2 items-center">
                
                <div className="col-span-1 flex justify-center">
                  <Skeleton
                    height={24}
                    width={24}
                    containerClassName="flex items-center"
                    baseColor="#d4d4d7"
                    highlightColor="#f4f4f5"
                  />
                </div>
                
                <div className="col-span-1">
                  <Skeleton
                    height={24}
                    containerClassName="h-6 flex items-center"
                    baseColor="#d4d4d7"
                    highlightColor="#f4f4f5"
                  />
                </div>
                
                <div className="col-span-3">
                  <Skeleton
                    height={24}
                    containerClassName="h-6 flex items-center"
                    baseColor="#d4d4d7"
                    highlightColor="#f4f4f5"
                  />
                </div>
                
                <div className="col-span-1">
                  <Skeleton
                    height={24}
                    containerClassName="h-6 flex items-center"
                    baseColor="#d4d4d7"
                    highlightColor="#f4f4f5"
                  />
                </div>
                
                <div className="col-span-6">
                  <Skeleton
                    height={24}
                    containerClassName="h-6 flex items-center"
                    baseColor="#d4d4d7"
                    highlightColor="#f4f4f5"
                  />
                </div>
              </div>
            </Repeat>
          )}

          {!isLoading && (data?.rankingByCat || []).map(({ GUID, UserName, TotalScore, tech }, index) => {
            const displayedIndex = page * ranksPerPage + index + 1
            const isChecked = selectedTalents.includes(GUID)

            return (
              <div
                key={`rank:${GUID}`}
                className="w-full grid grid-cols-12 gap-4 p-2 items-center hover:bg-gray-50 transition-colors"
              >
                <div className="col-span-1 flex justify-center">
                  <Checkbox
                    isLoading={false}
                    color="green"
                    unique={false}
                    values={[
                      {
                        key: GUID,
                        checked: isChecked,
                        value: ''
                      }
                    ]}
                    onCheck={(newValues) => {
                      handleRowCheckbox(newValues[0].checked, GUID)
                    }}
                  />
                </div>

                <div className="col-span-1 text-center flex justify-center items-center">
                  {displayedIndex > 3 ? (
                    <p className="font-semibold text-sm md:text-base">{displayedIndex}</p>
                  ) : (
                    <FontAwesomeIcon
                      icon={faMedal}
                      className={classNames('text-xl md:text-2xl', {
                        'text-yellow-400': displayedIndex === 1,
                        'text-slate-400': displayedIndex === 2,
                        'text-amber-700': displayedIndex === 3
                      })}
                    />
                  )}
                </div>

                <div className="col-span-3 text-center">
                  <Link
                    to={`/profile/details/${GUID}`}
                    className="w-full block text-inherit hover:text-blue-600 transition-colors"
                  >
                    <p className="text-sm md:text-base truncate px-2">{UserName}</p>
                  </Link>
                </div>

                <div className="col-span-1 text-center text-sm md:text-base">
                  {TotalScore} pts
                </div>

                <div className="col-span-6">
                  {tech && tech.length > 0 && (
                    <CardToggle
                      key={tech.Category}
                      title={i18next.t('categories')}
                      size="sm"
                    >
                      <div className="w-full overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead>
                            <tr>
                              <th className="px-4 py-2 text-left text-sm font-medium text-smorange-dark uppercase tracking-wider">
                                {i18next.t('category')}
                              </th>
                              <th className="px-4 py-2 text-left text-sm font-medium text-smorange-dark uppercase tracking-wider">
                                {i18next.t('total-score')}
                              </th>
                            </tr>
                          </thead>

                          <tbody className="bg-white divide-y divide-gray-200">
                            {tech.map((tech2) => (
                              tech2.Score != null && (
                                <tr key={tech2.Category}>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                    {tech2.Category}
                                  </td>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                    {tech2.Score}
                                  </td>
                                </tr>
                              )
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardToggle>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </Container>

      {!isLoading && data?.rankingByCat && data.rankingByCat.length > 0 && (
        <div className="w-full flex justify-center">
          <Pager pages={data.pages} page={page} setPage={setPage} />
        </div>
      )}
    </div>
  )
}

UserTopScore.propTypes = {
  categoriesList: PropTypes.arrayOf(PropTypes.string).isRequired,
  onLoadingChange: PropTypes.func
}

UserTopScore.defaultProps = {
  onLoadingChange: null
}

export default UserTopScore
